/**
 * File: RowControls.js
 * Origin: isotect
 * Description: RowControls component that renders action buttons for selected table rows.
 *
 * Version: v1.0.0
 * Created: 2026-05-09
 * Last Updated: 2026-06-24
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Local project files
import { IconButton, TextButton } from "../ui/Buttons";

// Icons
import { IoCheckboxOutline } from "react-icons/io5";
import { CgCloseR } from "react-icons/cg";

/**
 * Renders row-level action controls for a table.
 *
 * Provides configurable action buttons that operate on the currently
 * selected table rows. Actions may include operations such as deleting,
 * updating, exporting, or performing custom row-based workflows.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.rows - Current table row data
 * @param {Function} props.setTableRows - State setter used to update table rows
 * @param {string} props.primaryKey - Unique identifier field for each row
 * @param {Array<Object>} props.controlButtons - Configuration for the action buttons to render
 * @param {Array<Object>} props.selectedRows - Currently selected table rows
 * @param {Function} props.setSelectedRows - State setter used to update selected rows
 * @param {Function} props.setAllRowsSelected - State setter used to toggle the "all rows selected" state
 *
 * @returns {JSX.Element} A collection of row action controls.
 */
function RowControls({
    rows,
    setTableRows,
    primaryKey,
    controlButtons,
    selectedRows,
    setSelectedRows,
    setAllRowsSelected,
}) {
    const selectedRowCount = selectedRows.length;

    const handleClose = () => {
        setSelectedRows([]);
        setAllRowsSelected(false);
    };

    const handleActionClick = async (callback) => {
        const updates = await Promise.all(
            selectedRows.map(async (index) => {
                const result = await callback(rows[index][primaryKey]);

                return { index, result };
            }),
        );

        setTableRows((prevRows) => {
            const newRows = [...prevRows];

            updates.forEach(({ index, result }) => {
                newRows[index] = result.data;
            });

            return newRows;
        });

        setSelectedRows([]);
        setAllRowsSelected(false);
    };

    return (
        <>
            <div
                className={`row-controls ${selectedRowCount > 0 ? "row-controls--selected" : ""}`}
            >
                <div className="row-controls__selected-container">
                    <IconButton
                        icon={IoCheckboxOutline}
                        onClick={null}
                        size={24}
                    />
                    <div className="row-controls__selected-count">
                        <p>{selectedRowCount}</p>
                        <p>Item(s)</p>
                    </div>
                </div>
                <hr className="row-controls__hr"></hr>
                <div className="row-controls__direction-row">
                    {controlButtons.map((button, index) => {
                        if (button.type === "icon") {
                            return (
                                <IconButton
                                    key={index}
                                    {...button}
                                    onClick={null}
                                    size={24}
                                />
                            );
                        } else if (button.type === "text") {
                            let className = "";
                            if (button.text.toLowerCase() === "approve") {
                                className = "text-button--approve";
                            } else if (button.text.toLowerCase() === "reject") {
                                className = "text-button--reject";
                            }

                            return (
                                <TextButton
                                    key={index}
                                    {...button}
                                    onClick={() =>
                                        handleActionClick(button.onClick)
                                    }
                                    className={className}
                                />
                            );
                        }
                    })}
                </div>
                <hr className="row-controls__hr"></hr>
                <div className="row-controls__close-button">
                    <IconButton
                        icon={CgCloseR}
                        onClick={handleClose}
                        size={24}
                    />
                </div>
            </div>
        </>
    );
}

export default RowControls;
